<template>
  <div>
    <!-- Mise en forme et identification -->
    <div class="box box-primary box-menu-deroulant">
      <div class="box-header with-border">
        <h3 class="box-title">Facture</h3>
        <div class="box-tools pull-right">
          <champs-optionnels :options="facture.options"
                             :indisponibles="optionsIndisponibles"
                             @activer="$emit('activer-option', $event)">
          </champs-optionnels>
        </div>
      </div>
      <div class="box-body">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label for="modeleFacture">Modèle de facture</label>
              <select id="modeleFacture" class="form-control"
                      :value="facture.modele || ''"
                      @change="$emit('appliquer-modele', $event.target.value)">
                <option value="">Facture libre</option>
                <option v-for="modele in modelesDisponibles" :key="modele.cle" :value="modele.cle">
                  {{ modele.libelle }}
                </option>
              </select>
              <span class="help-block">
                Remplit les lignes du cas choisi. Les montants restés à 0 € sont à compléter.
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Mise en page</label>
              <div class="btn-group btn-group-justified" role="group">
                <div class="btn-group" role="group" v-for="modele in modeles" :key="modele.cle">
                  <button type="button" class="btn btn-sm"
                          :class="facture.template === modele.cle ? 'btn-primary' : 'btn-default'"
                          @click="modifier('template', modele.cle)">
                    {{ modele.libelle }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" :class="{'has-error': numeroEnDoublon}">
              <label for="numeroFacture">Numéro</label>
              <div class="input-group">
                <input type="text" id="numeroFacture" class="form-control"
                       :value="facture.numero"
                       :placeholder="exempleNumero"
                       @input="modifier('numero', $event.target.value)">
                <span class="input-group-btn">
                  <button type="button" class="btn btn-default"
                          title="Attribuer le prochain numéro disponible"
                          @click="$emit('numeroter')">
                    <i class="fa fa-magic"></i>
                  </button>
                </span>
              </div>
              <span v-if="numeroEnDoublon" class="help-block">
                Ce numéro est déjà utilisé par une facture de l'historique
              </span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" :class="{'has-error': !dateEmissionValide}">
              <label>Date d'émission</label>
              <div class="input-group date">
                <div class="input-group-addon"><i class="fa fa-calendar"></i></div>
                <input type="text" class="form-control pull-right" id="datepickerFactureEmission"
                       :value="facture.dateEmission"
                       @input="modifier('dateEmission', $event.target.value)"
                       @change="modifier('dateEmission', $event.target.value)">
              </div>
              <span v-if="!dateEmissionValide" class="help-block">Format attendu : jj/mm/aaaa</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12" v-if="facture.options.logo">
            <div class="form-group">
              <label>
                Logo
                <a href="#" class="retirer-champ" title="Retirer le logo"
                   @click.prevent="$emit('desactiver-option', 'logo')">&times;</a>
              </label>
              <input type="file" accept="image/png,image/jpeg" @change="$emit('logo-choisi', $event)">
              <span class="help-block" v-if="facture.emetteur.logoFederation">
                Logo de la fédération utilisé. Choisissez un fichier pour le remplacer
                (PNG ou JPG, 1 Mo maximum).
              </span>
              <span class="help-block" v-else>
                Logo personnalisé.
                <a href="#" @click.prevent="$emit('logo-federation')">Reprendre celui de la fédération</a>
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6" v-if="facture.options.echeance">
            <div class="form-group" :class="{'has-error': !dateEcheanceValide}">
              <label>
                Date d'échéance
                <a href="#" class="retirer-champ" title="Retirer ce champ"
                   @click.prevent="$emit('desactiver-option', 'echeance')">&times;</a>
              </label>
              <div class="input-group date">
                <div class="input-group-addon"><i class="fa fa-calendar"></i></div>
                <input type="text" class="form-control pull-right" id="datepickerFactureEcheance"
                       :value="facture.dateEcheance"
                       @input="modifier('dateEcheance', $event.target.value)"
                       @change="modifier('dateEcheance', $event.target.value)">
              </div>
              <span class="help-block">
                <a href="#" @click.prevent="$emit('echeance-jours', 30)">+ 30 jours</a> ·
                <a href="#" @click.prevent="$emit('echeance-jours', 15)">+ 15 jours</a>
              </span>
            </div>
          </div>
          <div class="col-md-6" v-if="facture.options.reference">
            <div class="form-group">
              <label>
                Référence / bon de commande
                <a href="#" class="retirer-champ" title="Retirer ce champ"
                   @click.prevent="$emit('desactiver-option', 'reference')">&times;</a>
              </label>
              <input type="text" class="form-control" :value="facture.reference"
                     @input="modifier('reference', $event.target.value)">
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- Client -->
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">Facturer à</h3>
      </div>
      <div class="box-body">
        <selection-client :client="facture.client"
                          :carnet="carnet"
                          @client-choisi="$emit('client-choisi', $event)"
                          @modifier="$emit('modifier-client', $event)"
                          @memoriser="$emit('memoriser-client')"
                          @oublier="$emit('oublier-client', $event)">
        </selection-client>
      </div>
    </div>

    <!-- Prestations -->
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">Prestations</h3>
      </div>
      <div class="box-body">
        <facture-lignes :lignes="facture.lignes"
                        :montants="totaux.montantsLignes"
                        :tva-par-ligne="tvaParLigne"
                        :colonne-tva="facture.options.tva"
                        :taux-global="facture.tva.taux"
                        :devise="facture.devise"
                        :suggestions="suggestions"
                        @ajouter="$emit('ajouter-ligne')"
                        @supprimer="$emit('supprimer-ligne', $event)"
                        @dupliquer="$emit('dupliquer-ligne', $event)"
                        @deplacer="$emit('deplacer-ligne', $event)"
                        @modifier="$emit('modifier-ligne', $event)">
        </facture-lignes>

        <hr>

        <div class="row">
          <!-- Remise -->
          <div class="col-md-4" v-if="facture.options.remise">
            <div class="form-group">
              <label>
                Remise
                <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'remise')">&times;</a>
              </label>
              <div class="input-group">
                <input type="text" class="form-control text-right" :value="facture.remise.valeur"
                       @input="modifier('remise.valeur', $event.target.value)">
                <div class="input-group-btn">
                  <button type="button" class="btn"
                          :class="facture.remise.type === 'pourcentage' ? 'btn-primary' : 'btn-default'"
                          @click="modifier('remise.type', 'pourcentage')">%</button>
                  <button type="button" class="btn"
                          :class="facture.remise.type === 'montant' ? 'btn-primary' : 'btn-default'"
                          @click="modifier('remise.type', 'montant')">€</button>
                </div>
              </div>
            </div>
          </div>

          <!-- TVA -->
          <div class="col-md-5" v-if="facture.options.tva">
            <div class="form-group">
              <label>
                TVA
                <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'tva')">&times;</a>
              </label>
              <div class="input-group">
                <select class="form-control" :value="facture.tva.mode"
                        @change="modifier('tva.mode', $event.target.value)">
                  <option value="global">Taux unique</option>
                  <option value="ligne">Par ligne</option>
                </select>
                <input v-if="facture.tva.mode === 'global'" type="text"
                       class="form-control text-right" :value="facture.tva.taux"
                       @input="modifier('tva.taux', $event.target.value)">
                <span class="input-group-addon" v-if="facture.tva.mode === 'global'">%</span>
              </div>
              <div v-if="facture.tva.mode === 'global'" class="taux-rapides">
                <button type="button" class="btn btn-default btn-xs"
                        v-for="taux in tauxCourants" :key="taux"
                        @click="modifier('tva.taux', taux)">{{ taux }} %</button>
              </div>
              <input type="text" class="form-control input-sm mention-exoneration"
                     :value="facture.tva.mentionExoneration"
                     placeholder="Mention, ex. : Exonéré de TVA (art. 44 CTVA)"
                     @input="modifier('tva.mentionExoneration', $event.target.value)">
            </div>
          </div>

          <!-- Frais -->
          <div class="col-md-3" v-if="facture.options.frais">
            <div class="form-group">
              <label>
                Frais
                <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'frais')">&times;</a>
              </label>
              <div class="input-group">
                <input type="text" class="form-control text-right" :value="facture.frais"
                       @input="modifier('frais', $event.target.value)">
                <span class="input-group-addon">€</span>
              </div>
            </div>
          </div>
        </div>

        <table class="table table-condensed table-recap">
          <tr v-if="detailVisible">
            <td>Sous-total</td>
            <td class="text-right">{{ fm(totaux.sousTotal) }}</td>
          </tr>
          <tr v-if="facture.options.remise">
            <td>Remise</td>
            <td class="text-right">- {{ fm(totaux.montantRemise) }}</td>
          </tr>
          <tr v-if="facture.options.tva">
            <td>TVA</td>
            <td class="text-right">{{ fm(totaux.montantTva) }}</td>
          </tr>
          <tr v-if="facture.options.frais">
            <td>Frais</td>
            <td class="text-right">{{ fm(totaux.frais) }}</td>
          </tr>
          <tr class="ligne-total">
            <td>Montant dû</td>
            <td class="text-right">{{ fm(totaux.total) }}</td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Mentions et paiement -->
    <div class="box box-primary" v-if="blocMentionsVisible">
      <div class="box-header with-border">
        <h3 class="box-title">Mentions et paiement</h3>
      </div>
      <div class="box-body">
        <div class="form-group" v-if="facture.options.conditions">
          <label>
            Conditions de paiement
            <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'conditions')">&times;</a>
          </label>
          <textarea class="form-control" rows="2" :value="facture.conditionsPaiement"
                    placeholder="Paiement à 30 jours..."
                    @input="modifier('conditionsPaiement', $event.target.value)"></textarea>
        </div>

        <div class="form-group" v-if="facture.options.notes">
          <label>
            Note
            <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'notes')">&times;</a>
          </label>
          <textarea class="form-control" rows="2" :value="facture.notes"
                    @input="modifier('notes', $event.target.value)"></textarea>
        </div>

        <div v-if="facture.options.paiement">
          <label>
            Coordonnées de paiement
            <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'paiement')">&times;</a>
          </label>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <input type="text" class="form-control" :value="facture.paiement.beneficiaire"
                       placeholder="Bénéficiaire"
                       @input="modifier('paiement.beneficiaire', $event.target.value)">
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <input type="text" class="form-control" :value="facture.paiement.iban"
                       placeholder="IBAN"
                       @input="modifier('paiement.iban', $event.target.value)">
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <input type="text" class="form-control" :value="facture.paiement.bic"
                       placeholder="BIC"
                       @input="modifier('paiement.bic', $event.target.value)">
              </div>
            </div>
            <div class="col-md-8">
              <div class="form-group" :class="{'has-error': communicationInvalide}">
                <div class="input-group">
                  <input type="text" class="form-control"
                         :value="facture.paiement.communicationStructuree"
                         placeholder="+++000/0000/00000+++"
                         @input="modifier('paiement.communicationStructuree', $event.target.value)">
                  <span class="input-group-btn">
                    <button type="button" class="btn btn-default"
                            title="Générer depuis le numéro de facture"
                            @click="$emit('generer-communication')">
                      <i class="fa fa-magic"></i>
                    </button>
                  </span>
                </div>
                <span v-if="communicationInvalide" class="help-block">
                  Communication structurée invalide (clé de contrôle)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group" v-if="facture.options.qr">
          <label>
            QR de paiement SEPA
            <a href="#" class="retirer-champ" @click.prevent="$emit('desactiver-option', 'qr')">&times;</a>
          </label>
          <p class="help-block" v-if="qrIndisponible">
            Génération de QR indisponible sur cette installation.
          </p>
          <p class="help-block" v-else-if="!qrPossible">
            Renseignez le bénéficiaire, l'IBAN et un total supérieur à 0 pour générer le QR.
          </p>
          <p class="help-block" v-else>
            <i class="fa fa-check text-green"></i> QR généré et ajouté à la facture.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import FactureLignes from './FactureLignes'
  import ChampsOptionnels from './ChampsOptionnels'
  import SelectionClient from './SelectionClient'
  import {formaterMontant} from '../../../utils/facture/calculs'
  import {dateFrValide} from '../../../utils/facture/modele'
  import {communicationStructureeValide, formatLisible} from '../../../utils/facture/numerotation'
  import {MODELES} from '../../../utils/facture/modeles'

  require('bootstrap-datepicker/js/bootstrap-datepicker')
  require('bootstrap-datepicker/js/locales/bootstrap-datepicker.fr')

  export default {
    name: 'FactureEditeur',
    components: {FactureLignes, ChampsOptionnels, SelectionClient},
    props: {
      facture: {type: Object, required: true},
      totaux: {type: Object, required: true},
      suggestions: {type: Array, default: function () { return [] }},
      carnet: {type: Array, default: function () { return [] }},
      optionsIndisponibles: {type: Array, default: function () { return [] }},
      numeroEnDoublon: {type: Boolean, default: false},
      qrPossible: {type: Boolean, default: false}
    },
    data () {
      return {
        modeles: [
          {cle: 'moderne', libelle: 'Moderne'},
          {cle: 'classique', libelle: 'Classique'},
          {cle: 'minimal', libelle: 'Minimal'}
        ],
        tauxCourants: [0, 6, 21]
      }
    },
    computed: {
      modelesDisponibles () {
        return MODELES
      },
      /** Exemple au format de numérotation en vigueur, préfixe compris. */
      exempleNumero () {
        return formatLisible()
          .replace('AAAA', String(new Date().getFullYear()))
          .replace(/N+/, function (rang) {
            let exemple = '1'
            while (exemple.length < rang.length) exemple = '0' + exemple
            return exemple
          })
      },
      tvaParLigne () {
        return this.facture.options.tva && this.facture.tva.mode === 'ligne'
      },
      detailVisible () {
        return this.facture.options.tva || this.facture.options.remise || this.facture.options.frais
      },
      blocMentionsVisible () {
        const options = this.facture.options
        return options.notes || options.conditions || options.paiement || options.qr
      },
      dateEmissionValide () {
        return dateFrValide(this.facture.dateEmission)
      },
      dateEcheanceValide () {
        return !this.facture.dateEcheance || dateFrValide(this.facture.dateEcheance)
      },
      qrIndisponible () {
        return this.optionsIndisponibles.indexOf('qr') !== -1
      },
      communicationInvalide () {
        const communication = this.facture.paiement.communicationStructuree
        return !!communication && !communicationStructureeValide(communication)
      }
    },
    watch: {
      // Le champ échéance n'existe dans le DOM qu'une fois l'option activée
      'facture.options.echeance' (actif) {
        if (actif) this.$nextTick(this.initialiserDatepickerEcheance)
      }
    },
    mounted () {
      this.initialiserDatepickerEmission()
      if (this.facture.options.echeance) this.initialiserDatepickerEcheance()
    },
    methods: {
      fm (valeur) {
        return formaterMontant(valeur, this.facture.devise)
      },
      modifier (chemin, valeur) {
        this.$emit('modifier', {chemin: chemin, valeur: valeur})
      },
      initialiserDatepickerEmission () {
        $('#datepickerFactureEmission').datepicker({
          language: 'fr',
          format: 'dd/mm/yyyy',
          autoclose: true,
          todayHighlight: true
        })
      },
      initialiserDatepickerEcheance () {
        $('#datepickerFactureEcheance').datepicker({
          language: 'fr',
          format: 'dd/mm/yyyy',
          autoclose: true,
          todayHighlight: true
        })
      }
    }
  }
</script>

<style scoped>
  /*
   * Les boxes AdminLTE sont en overflow:hidden : le menu « + Ajouter », posé
   * dans l'en-tête de celle-ci, s'y trouvait coupé net dès qu'il dépassait le
   * bas de la box. On lève le rognage sur cette box précise, pas sur toutes.
   */
  .box-menu-deroulant {
    overflow: visible;
  }

  .retirer-champ {
    color: #dd4b39;
    font-weight: 700;
    margin-left: 4px;
    text-decoration: none;
  }

  .taux-rapides {
    margin-top: 5px;
  }

  .taux-rapides .btn {
    margin-right: 4px;
  }

  .mention-exoneration {
    margin-top: 8px;
  }

  .table-recap {
    max-width: 330px;
    margin-left: auto;
    margin-bottom: 0;
  }

  .table-recap td {
    border-top: none !important;
  }

  .ligne-total td {
    border-top: 2px solid #ddd !important;
    font-size: 15px;
    font-weight: 700;
  }
</style>




// WEBPACK FOOTER //
// src/components/Outils/Facture/FactureEditeur.vue